import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { CosignatureQueueResponse, CosignatureQueueSearchParams } from "@/core/types";
import { SHORT_LIVED_CACHE } from "@/lib/constants/cache";

import { cosignatureKeys } from "../keyFactory";
import { getCosignatureQueue } from "../queryFunctions/getCosignatureQueue";

type UseCosignatureQueueProps<TData = CosignatureQueueResponse> = {
  enabled?: boolean;
  searchParams?: CosignatureQueueSearchParams;
  select?: (data: CosignatureQueueResponse) => TData;
};

type CosignatureQueueQueryOptions = UseQueryOptions<
  CosignatureQueueResponse,
  unknown,
  CosignatureQueueResponse,
  ReturnType<typeof cosignatureKeys.queue>
>;

export function optionFactory(searchParams?: CosignatureQueueSearchParams, enabled = true) {
  return {
    enabled,
    queryFn: ({ signal }) => getCosignatureQueue(searchParams, signal),
    queryKey: cosignatureKeys.queue(searchParams),
    refetchOnWindowFocus: true,
    staleTime: SHORT_LIVED_CACHE,
  } satisfies CosignatureQueueQueryOptions;
}

export function useCosignatureQueue<TData = CosignatureQueueResponse>({
  enabled,
  searchParams,
  select,
}: UseCosignatureQueueProps<TData> = {}) {
  return useQuery({ ...optionFactory(searchParams, enabled), keepPreviousData: true, select });
}
